/**
 * Dashboard.tsx — the Command Center (mockup carbon/01, blueprint §5.2 cc).
 *
 * Visual flow:
 *   1. The brief: BriefHero (the action-first h1, the lede, the view's one
 *      primary action beside a tertiary, the provenance line) beside the
 *      Guardian Score tile (verdict, delta tag, the 100-point waffle).
 *   2. PostureTabs: Posture · Operations · History as line tabs, with the
 *      small view actions (commit, download, refresh) beside them.
 *   3. Tab content
 *      - Posture    → four pillar tiles in a condensed grid, the "What to
 *                     close this week" DataTable, then Recent changes beside
 *                     the 90-day trend
 *      - Operations → OperationsPanel
 *      - History    → placeholder
 *
 * Copy comes from the posture composer (usePostureBrief) and reaches the page
 * only through BriefHero (§4.7). Composer inputs follow the known-value rule
 * (blueprint "§5.2 wiring note"): a count is `undefined` while its source is
 * loading, failed or absent, never a fabricated 0.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { toActionBrief } from '@/lib/brief-shape'
import {
  calculateStarredBpaScore,
  calculateUnifiedGuardianScore,
  useComplianceDashboard,
  useComplianceScoreHistory,
  useComplianceScores,
} from '@/hooks/useComplianceData'
import { useTenantContext } from '@/hooks/useTenantContext'
import { usePostureBrief } from '@/hooks/usePostureBrief'
import { usePriorityActions } from '@/hooks/usePriorityActions'
import { useTenantScanSchedule } from '@/hooks/useTenantScanSchedule'
import { canEditScanSchedule } from '@/lib/scan-schedule'

import {
  BriefHero,
  Button,
  ButtonLink,
  CondensedGrid,
  IconArrowRight,
  PointGrid,
  ScoreTile,
  ScoreWaffle,
  Skeleton,
  Tag,
  Tile,
} from '@/components/carbon'
import DashboardEmptyState from '@/components/Dashboard/DashboardEmptyState'
import QuickActions from '@/components/Dashboard/QuickActions'
import EmptyState from '@/components/shared/EmptyState'

import PostureTabs, { useDashboardTab } from '@/components/Dashboard/v2/PostureTabs'
import PillarCompliance from '@/components/Dashboard/v2/PillarCompliance'
import PillarBpa from '@/components/Dashboard/v2/PillarBpa'
import PillarIdentity from '@/components/Dashboard/v2/PillarIdentity'
import { PillarTile } from '@/components/Dashboard/v2/PillarShell'
import PriorityQueue from '@/components/Dashboard/v2/PriorityQueue'
import ActivityStrip from '@/components/Dashboard/v2/ActivityStrip'
import OperationsPanel from '@/components/Dashboard/v2/OperationsPanel'
import HistoryPanel from '@/components/Dashboard/v2/HistoryPanel'
import {
  commandCenterProvenance,
  GUARDIAN_WEIGHTS,
  guardianComponents,
  scoreDeltaSince,
  waffleSegments,
} from '@/components/Dashboard/v2/cc-score'
import { composeScoreHistory, HISTORY_DAYS, pillarDeltas } from '@/components/Dashboard/v2/cc-history'

function deltaTag(text: string | null): React.ReactNode {
  return text ? <Tag className="tabular-nums">{text}</Tag> : undefined
}

export default function Dashboard() {
  const { defaultTenantId, isLoading: contextLoading, userTenants, isGlobalAdmin } = useTenantContext()
  const [selectedTenantId, setSelectedTenantId] = useState<string | undefined>(undefined)
  // Resolve selected tenant once context loads. Global admins see an empty
  // state until they pick; regular users default to their own tenant.
  useEffect(() => {
    if (contextLoading) return
    if (isGlobalAdmin) {
      if (defaultTenantId) setSelectedTenantId(defaultTenantId)
      // else leave undefined — DashboardEmptyState points at the switcher
    } else {
      if (defaultTenantId !== undefined) {
        setSelectedTenantId(defaultTenantId)
      } else if (userTenants.length > 0) {
        setSelectedTenantId(userTenants[0].tenantId)
      }
    }
  }, [contextLoading, defaultTenantId, isGlobalAdmin, userTenants])

  // Tenant changes propagate from the side nav's tenant switcher via
  // saveTenantSelection() + page reload — no per-page handler needed.
  // DashboardEmptyState ignores its onTenantSelect prop now (kept for API
  // back-compat); we pass a no-op.
  const handleTenantChange = () => {}

  // ---- Data queries (the same keys as the other posture pages, so the cache
  //      stays warm when the user moves between them) ----

  const {
    data: dashboardStats,
    isLoading: statsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery({
    queryKey: ['dashboard', selectedTenantId],
    queryFn: () => (selectedTenantId ? api.getDashboardStats(selectedTenantId) : Promise.resolve(null)),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: secureScoreSummary, isLoading: secureScoreLoading } = useQuery({
    queryKey: ['secure-score-summary', selectedTenantId],
    queryFn: () => api.getSecureScoreSummary(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: userInsights, isLoading: userInsightsLoading } = useQuery({
    queryKey: ['user-insights', selectedTenantId],
    queryFn: () => api.getUserInsights(selectedTenantId!),
    enabled: !!selectedTenantId,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: complianceScores, isLoading: complianceScoresLoading } =
    useComplianceScores(selectedTenantId)

  // The violation summary behind the Best Practice facts (failing and critical
  // counts). Shares its queries with the Best Practice tile.
  const { dashboardData: complianceDashboard } = useComplianceDashboard(selectedTenantId)

  // Score history for the trend tile, the Guardian Score delta tag (§7 C6)
  // and the pillar tiles' "+N in 90 days" tags (§7 C9), from the lightweight
  // history endpoints (cc-history.ts). Never the executive report: it runs an
  // Azure OpenAI narrative on every uncached load (Adam, 2026-10-05). The
  // score history shares its key with useComplianceDashboard's own call.
  const { data: bpaHistory, isLoading: bpaHistoryLoading } = useComplianceScoreHistory(selectedTenantId, HISTORY_DAYS)
  const { data: metricTrends, isLoading: metricTrendsLoading } = useQuery({
    queryKey: ['metric-trends', selectedTenantId],
    queryFn: () => api.getDashboardTrends(selectedTenantId!),
    enabled: !!selectedTenantId,
    staleTime: 5 * 60 * 1000,
  })

  // Starred BPA score — only includes frameworks the user has opted into the
  // composite. Reads `weightedScore` (criticality-weighted) so the brief and
  // the waffle match the Best Practice tile's score, which sources the same
  // field via `transformToDisplayFrameworks`.
  const bpaStarredScore = useMemo(() => {
    if (!complianceScores?.standardScores) return undefined
    const mapped = complianceScores.standardScores.map(s => ({
      code: s.standardCode,
      score: s.weightedScore,
      weight: s.weight,
      includeInGuardianScore: s.includeInGuardianScore,
    }))
    return calculateStarredBpaScore(mapped) ?? undefined
  }, [complianceScores?.standardScores])

  // The four components, each null while unknown (never a placeholder 0), so
  // the Guardian Score, the waffle and the tiles read the same numbers.
  const components = useMemo(
    () =>
      guardianComponents({
        stats: dashboardStats,
        insights: userInsights,
        bpa: bpaStarredScore,
        secure: secureScoreSummary?.score?.percentage,
      }),
    [dashboardStats, userInsights, bpaStarredScore, secureScoreSummary?.score?.percentage],
  )

  const guardianScore = useMemo(
    () =>
      calculateUnifiedGuardianScore({
        secureScore: components.secure,
        bpaScore: components.bpa,
        mfaAdoption: components.identity,
        configCompliance: components.baseline,
      }),
    [components],
  )

  const segments = useMemo(() => waffleSegments(components), [components])

  const history = useMemo(
    () => composeScoreHistory({ bpa: bpaHistory, metrics: metricTrends }, components),
    [bpaHistory, metricTrends, components],
  )
  const deltas = useMemo(() => pillarDeltas(history, components), [history, components])

  // Top priority action — feeds the brief so the headline names the same fix
  // the queue ranks #1, and the brief's primary button opens it. One row at
  // pillar='all' so the brief speaks to whichever pillar currently leads.
  const { actions: topActions } = usePriorityActions({
    tenantId: selectedTenantId,
    maxCount: 1,
    pillar: 'all',
  })
  const topAction = topActions[0]
  const topActionForBrief = useMemo(() => {
    if (!topAction) return undefined
    // posture-brief.ts only models {crit, high, med} for impact emphasis —
    // narrow low/routine down to 'med' rather than dropping the action,
    // since the title is the load-bearing signal regardless.
    const impact: 'crit' | 'high' | 'med' =
      topAction.severity === 'crit' ? 'crit'
      : topAction.severity === 'high' ? 'high'
      : 'med'
    return {
      pillar: topAction.pillar,
      title: topAction.title,
      impact,
    }
  }, [topAction])

  const pillarScores = useMemo(
    () => ({
      compliance: components.baseline ?? undefined,
      bpa: components.bpa ?? undefined,
      mfa: components.identity ?? undefined,
      secureScore: components.secure ?? undefined,
    }),
    [components],
  )

  // Pillar facts for the action-first lede (blueprint §6.2 row 1, §5.2
  // wiring note): undefined while unknown, never a fabricated 0. The no-MFA
  // count is untrusted while Exchange classification is refining (shared and
  // resource mailboxes count as members), the same gate PillarIdentity and
  // knownMfaPct apply.
  const d = complianceDashboard
  const mfaRefining = userInsights?.hasData === true && userInsights.mailboxClassificationAvailable === false
  const briefLoading =
    statsLoading || secureScoreLoading || userInsightsLoading || complianceScoresLoading
  // Nothing assessed yet: every component is unknown once the queries settle.
  const noData = !briefLoading && guardianScore == null

  // A new tenant's first scan comes from its schedule (TenantSchedulingService),
  // and only a Platform Admin may trigger one (ScanController, the /admin/scans
  // route guard). So the no-data brief links Ad-Hoc Scans for that role only.
  // Same cached /me read as useTenantContext.
  const { data: currentUser, isLoading: currentUserLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: api.getCurrentUser,
    staleTime: 5 * 60 * 1000,
  })
  const canRunScans = currentUser?.isPlatformAdmin === true
  // Anyone else learns what turns the first assessment on (finding 5), from
  // the tenant's schedule: a viewer who may edit it gets Tenant Management
  // once it is known off. The role fails closed while /me is unknown.
  const scanSchedule = useTenantScanSchedule({
    tenantId: selectedTenantId,
    neverScanned: noData,
    currentUser,
    currentUserLoading,
  })
  const scheduleSettingsHref = noData && !canRunScans && canEditScanSchedule(currentUser) ? '/tenants' : undefined

  const brief = usePostureBrief({
    tenantId: selectedTenantId,
    score: guardianScore,
    pillarScores,
    topAction: topActionForBrief,
    facts: {
      identity: {
        noMfaMembers: mfaRefining ? undefined : userInsights?.noMfaEnabledUsers,
        noMfaGlobalAdmins: mfaRefining ? undefined : userInsights?.noMfaGlobalAdmins,
      },
      bpa: {
        failing: d?.totalViolationsKnown ? d.totalViolations : undefined,
        critical: d?.totalViolationsKnown ? d.criticalViolations : undefined,
      },
      compliance: {
        driftedResources: dashboardStats?.activeDriftsKnown ? dashboardStats.activeDrifts : undefined,
      },
    },
    // The composer owns no routes: the brief's one primary action opens the
    // queue's #1 item, and its label names the headline's subject.
    primaryHref: noData ? (canRunScans ? '/admin/scans' : undefined) : topAction?.href,
    noData,
    scheduleSettingsHref,
    scanSchedule,
  })

  const [tab] = useDashboardTab()

  // ---- Provenance timestamp ----
  // Scans run automatically; we surface the most-recent freshness signal already
  // in scope rather than inventing a new DTO field: the newer of the Secure
  // Score fetch and the user-insights refresh. DashboardStats has no top-level
  // timestamp today — if one is added (e.g. DashboardStats.lastFullAssessmentAt)
  // wire it in here.
  const lastAssessedAt = useMemo(() => {
    const candidates: (string | null | undefined)[] = [
      secureScoreSummary?.fetchedAt,
      userInsights?.lastRefreshedAt,
    ]
    const valid = candidates
      .filter((t): t is string => !!t)
      .map(t => new Date(t).getTime())
      .filter(n => !isNaN(n))
    if (valid.length === 0) return null
    return new Date(Math.max(...valid)).toISOString()
  }, [secureScoreSummary?.fetchedAt, userInsights?.lastRefreshedAt])

  const tenantName = userTenants.find(t => t.tenantId === selectedTenantId)?.name
  const provenance = commandCenterProvenance({ at: lastAssessedAt, tenantName })

  // ---- Early returns: error / loading / empty tenant state ----

  if (statsError) {
    return (
      <section aria-labelledby="cc-error" className="mt-8 max-w-[56rem]">
        <h1 id="cc-error" className="m-0 text-headline-sm font-light text-text-primary max-c-md:text-[2rem]">
          Reload the Command Center.
        </h1>
        <p className="m-0 mt-6 max-w-[56ch] text-heading font-light text-text-secondary">
          <b className="font-semibold text-text-primary">The dashboard data did not load</b>, so Guardian has no score
          to show yet.
        </p>
        <Button kind="primary" className="mt-8" icon={<IconArrowRight />} onClick={() => refetchStats()}>
          Retry
        </Button>
      </section>
    )
  }

  if (contextLoading) {
    return (
      <div className="mt-8 max-w-[56rem]">
        <Skeleton lines={3} label="Loading your tenants" />
      </div>
    )
  }

  if (!selectedTenantId && isGlobalAdmin && userTenants.length > 0) {
    return (
      <DashboardEmptyState
        tenants={userTenants}
        selectedTenantId={selectedTenantId}
        onTenantSelect={handleTenantChange}
      />
    )
  }

  if (!selectedTenantId && userTenants.length === 0) {
    return (
      <section aria-label="No tenant" className="mt-8 max-w-[56rem]">
        <Tile padding={24}>
          {/* The no-tenant state is the page's only content, so its headline is the h1. */}
          {isGlobalAdmin ? (
            <EmptyState
              headline="Add a tenant to start monitoring."
              body="Guardian assesses a Microsoft 365 tenant once it is added and consented. The Command Center then shows its score and what to close first."
              cta={{ label: 'Add a tenant', to: '/admin?tab=tenants' }}
              headingLevel="h1"
            />
          ) : (
            <EmptyState
              headline="Ask your administrator for access to a tenant."
              body="No tenant is linked to your account yet. The Command Center fills in once an administrator grants you one."
              headingLevel="h1"
            />
          )}
        </Tile>
      </section>
    )
  }
  // ---- Main render ----

  const verdict = guardianScore == null ? undefined : toActionBrief(brief).verdict
  const secureEarned = segments.find(s => s.category === 'secure')?.earned ?? null

  // The primary action is the brief's own (BriefHero renders it first); this is the tertiary beside it.
  const briefActions = (
    <ButtonLink to="/executive-report" kind="tertiary" icon={<IconArrowRight />}>
      Open Executive Report
    </ButtonLink>
  )

  return (
    <div>
      <section
        aria-labelledby="brief-verdict"
        className="mt-8 grid grid-cols-1 items-stretch gap-8 c-xlg:grid-cols-[minmax(0,1fr)_minmax(0,560px)]"
      >
        <BriefHero
          brief={brief}
          size="hero"
          headingId="brief-verdict"
          isLoading={briefLoading}
          // A never-assessed tenant has no report to open.
          secondaryAction={noData ? undefined : briefActions}
          provenance={noData ? undefined : provenance}
          className="[overflow-wrap:anywhere]"
        />
        <ScoreTile
          size="hero"
          title="Guardian Score"
          score={guardianScore}
          verdict={verdict}
          delta={deltaTag(scoreDeltaSince(history))}
          isLoading={briefLoading}
          aside={briefLoading ? <Skeleton lines={4} width="158px" /> : <ScoreWaffle segments={segments} />}
          className="max-c-xlg:max-w-[640px]"
        />
      </section>

      <PostureTabs
        actions={
          <QuickActions
            selectedTenantId={selectedTenantId}
            pendingProcessingCount={dashboardStats?.pendingProcessing ?? 0}
            onRefresh={() => refetchStats()}
            isRefreshing={statsLoading}
            noData={noData}
          />
        }
      />

      {tab === 'posture' && (
        <div id="panel-posture" role="tabpanel" aria-labelledby="tab-posture" className="mt-8">
          <CondensedGrid as="section" aria-label="Pillars" columns={{ base: 1, 'c-md': 2, 'c-xlg': 4 }}>
            <PillarCompliance
              tenantId={selectedTenantId}
              stats={dashboardStats}
              isLoading={statsLoading}
              delta={deltaTag(deltas.baseline)}
            />
            <PillarBpa tenantId={selectedTenantId} delta={deltaTag(deltas.bpa)} />
            <PillarIdentity insights={userInsights} isLoading={userInsightsLoading} delta={deltaTag(deltas.identity)} />
            <PillarTile
              title="Secure Score"
              category="secure"
              to="/security?tab=secure-score"
              linkLabel="Open Secure Score"
              score={components.secure}
              isLoading={secureScoreLoading}
              delta={deltaTag(deltas.secure)}
              finding={
                components.secure == null
                  ? 'Microsoft Secure Score has not synced for this tenant yet.'
                  : `Read from Microsoft for your tenant. Guardian counts it as ${GUARDIAN_WEIGHTS.secure} of the 100 points.`
              }
              viz={
                secureEarned == null ? null : (
                  <>
                    <p className="m-0 mb-2 text-helper text-text-helper">
                      {secureEarned} of {GUARDIAN_WEIGHTS.secure} points toward your Guardian Score
                    </p>
                    <PointGrid earned={secureEarned} total={GUARDIAN_WEIGHTS.secure} />
                  </>
                )
              }
            />
          </CondensedGrid>

          <PriorityQueue tenantId={selectedTenantId} noData={noData} />

          <ActivityStrip
            stats={dashboardStats}
            isLoading={statsLoading}
            trendPoints={history}
            trendLoading={bpaHistoryLoading || metricTrendsLoading}
          />
        </div>
      )}

      {tab === 'operations' && (
        <OperationsPanel
          tenantId={selectedTenantId}
          guardianScore={guardianScore}
          stats={dashboardStats}
          insights={userInsights}
          bpaScore={bpaStarredScore}
        />
      )}

      {tab === 'history' && <HistoryPanel />}
    </div>
  )
}
